'use client'
// Bandeau global affiché pendant une impersonation admin OU un « voir comme ».
// Permet de revenir à son compte admin en un clic. Monté dans app/layout.js.
import { useEffect, useState } from 'react'
import { getImpersonationState, stopImpersonation } from '@/app/lib/impersonation'
import { getViewAs, stopViewAs } from '@/app/lib/view-as'
import { getCurrentLang } from '@/app/lib/lang-context'

// Le bandeau est monté dans app/layout.js, HORS du LangProvider : il lit la langue
// dans localStorage et porte ses 8 textes lui-même (audit NL 23/10/2026 — il restait
// en français sur un dossier en néerlandais).
const TXT = {
  fr: { vue: 'Vue', session: ' · votre session admin est intacte', quitter: '← Quitter la vue client',
        connecte: 'Connecté en tant que', admin: ' — admin : ', revenir: '← Revenir à mon compte admin',
        utilisateur: 'utilisateur', impossible: 'Impossible de revenir : ', erreur: 'erreur' },
  nl: { vue: 'Weergave', session: ' · uw adminsessie blijft intact', quitter: '← Klantweergave verlaten',
        connecte: 'Aangemeld als', admin: ' — admin: ', revenir: '← Terug naar mijn adminaccount',
        utilisateur: 'gebruiker', impossible: 'Terugkeren is niet gelukt: ', erreur: 'fout' },
}

export default function ImpersonationBanner() {
  const [state, setState] = useState(null)
  const [vue, setVue] = useState(null)
  const [busy, setBusy] = useState(false)
  const [langue, setLangue] = useState('fr')
  const L = TXT[langue] || TXT.fr

  useEffect(() => { setState(getImpersonationState()); setVue(getViewAs()); setLangue(getCurrentLang()) }, [])

  // « Voir comme le client » : l'admin est TOUJOURS sur sa propre session, seul
  // l'affichage est restreint. Rien à restaurer, on coupe et on recharge.
  // Prioritaire sur l'impersonation : les deux ne coexistent pas en pratique,
  // et c'est ce mode-ci qu'on veut voir signalé si jamais ils se chevauchaient.
  if (vue) {
    return (
      <>
        <style>{`@media print{.au-impersonation-banner{display:none!important}}`}</style>
        <div className="no-print au-impersonation-banner" style={{
          position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
          zIndex: 2147483647, display: 'flex', alignItems: 'center', gap: 14,
          background: '#0d1a14', color: '#6ee7b7', border: '1px solid #10b981',
          borderRadius: 12, padding: '10px 16px', boxShadow: '0 8px 30px rgba(0,0,0,.5)',
          fontFamily: "'Inter',system-ui,sans-serif", fontSize: 13, maxWidth: '92vw',
        }}>
          <span style={{ fontSize: 18 }}>👁</span>
          <span>
            {L.vue} <strong>{vue.role}</strong>{vue.clientNom ? <> — {vue.clientNom}</> : null}
            <span style={{ opacity: .7 }}>{L.session}</span>
          </span>
          <button
            onClick={() => { stopViewAs(); window.location.href = '/' }}
            style={{
              background: '#10b981', color: '#04120c', border: 'none', borderRadius: 8,
              padding: '7px 14px', fontWeight: 700, fontSize: 13, cursor: 'pointer', whiteSpace: 'nowrap',
            }}
          >
            {L.quitter}
          </button>
        </div>
      </>
    )
  }

  if (!state) return null

  async function back() {
    setBusy(true)
    try { await stopImpersonation() }
    catch (e) { setBusy(false); alert(L.impossible + (e?.message || L.erreur)) }
  }

  return (
    <>
      {/* Ce bandeau ne doit JAMAIS s'imprimer (il se retrouvait sur les PDF de
          documents — écritures/charges). Règle print locale garantie : le
          composant est monté dans app/layout.js, hors du <style> print du
          dashboard, donc on embarque la sienne. */}
      <style>{`@media print{.au-impersonation-banner{display:none!important}}`}</style>
      <div className="no-print au-impersonation-banner" style={{
      position: 'fixed', bottom: 16, left: '50%', transform: 'translateX(-50%)',
      zIndex: 2147483647, display: 'flex', alignItems: 'center', gap: 14,
      background: '#1a0d0d', color: '#fbbf24',
      border: '1px solid #f59e0b', borderRadius: 12,
      padding: '10px 16px', boxShadow: '0 8px 30px rgba(0,0,0,.5)',
      fontFamily: "'Inter',system-ui,sans-serif", fontSize: 13, maxWidth: '92vw',
    }}>
      <span style={{ fontSize: 18 }}>🎭</span>
      <span>
        {L.connecte} <strong>{state.target_email || L.utilisateur}</strong>
        {state.admin_email ? <span style={{ opacity: .7 }}>{L.admin}{state.admin_email}</span> : null}
      </span>
      <button onClick={back} disabled={busy} style={{
        background: '#fbbf24', color: '#000', border: 'none', borderRadius: 8,
        padding: '7px 14px', fontWeight: 700, fontSize: 13,
        cursor: busy ? 'wait' : 'pointer', whiteSpace: 'nowrap',
      }}>
        {busy ? '⏳…' : L.revenir}
      </button>
    </div>
    </>
  )
}
